import { createSelector } from '@reduxjs/toolkit';
import {
  selectActiveLibraryType,
  selectContentLibraryActiveState,
  selectContentLibraryPermissions,
  selectContentLibraryProcessingMeta,
  selectFiles,
  selectFolders,
  selectRoomLibrary,
  selectRootFolder,
  selectSharedFiles,
  selectSharedFolder,
} from 'features/content-library/contentLibrarySlice';

export const selectContentLibraryHasSharedContent = createSelector(
  [selectSharedFolder],
  (sharedFolder) => sharedFolder.childFolderIds.length > 0 || sharedFolder.childFileIds.length > 0
);

export const selectContentLibraryHasContent = createSelector(
  [selectRootFolder, selectActiveLibraryType, selectContentLibraryHasSharedContent],
  (rootFolder, activeLibraryType, sharedFolderHasContent) => {
    const hasRegularContent =
      rootFolder.childFolderIds.length > 0 ||
      rootFolder.childFileIds.length > 0 ||
      rootFolder.childUploadIds.length > 0;

    // Only include shared content for non-personal libraries (room libraries)
    const hasSharedContent = activeLibraryType !== 'personal' ? sharedFolderHasContent : false;

    return hasRegularContent || hasSharedContent;
  }
);

export const selectContentLibraryCore = createSelector(
  [selectFolders, selectFiles, selectContentLibraryProcessingMeta, selectSharedFiles],
  (folders, files, processingMeta, sharedFiles) => ({
    folders,
    files,
    processingMeta,
    sharedFiles,
  })
);

// Combined selector for all content library data needed for content operations
export const selectContentLibraryData = createSelector(
  [selectContentLibraryCore, selectContentLibraryActiveState, selectContentLibraryPermissions],
  (core, activeState, permissions) => ({
    ...core,
    ...activeState,
    ...permissions,
  })
);

export const selectRoomLibraryHasClearableContent = createSelector(
  [selectRoomLibrary, selectContentLibraryHasSharedContent],
  (roomLibrary, hasSharedContent) => {
    const { root } = roomLibrary.folders;

    // Whiteboards are excluded because the clear dialog cannot delete them — a
    // whiteboard-only library would otherwise show a trash icon that clears nothing.
    const hasClearableFiles = root.childFileIds.some(
      (fileId) => roomLibrary.files[fileId]?.type !== 'whiteboard'
    );

    return (
      root.childFolderIds.length > 0 ||
      root.childUploadIds.length > 0 ||
      hasClearableFiles ||
      hasSharedContent
    );
  }
);
